import { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import {
  Link2,
  FileText,
  X,
  ExternalLink,
  Copy,
  Check,
  Pencil,
  Trash2,
} from 'lucide-react'
import { Tag, VaultItem } from '../../config'
import { Modal, useModalClose } from '../ui/Modal'
import { gradientFor } from '../../lib/folderColors'
import { formatDate, safeHref } from '../../lib/items'

interface ItemDetailProps {
  item: VaultItem
  tags: Tag[]
  folderName: string | null
  onClose: () => void
  onEdit: () => void
  onDelete: () => void
}

export function ItemDetail(props: ItemDetailProps) {
  return (
    <Modal
      onClose={props.onClose}
      label={props.item.title}
      glow={gradientFor(props.item.folderId)[0]}
    >
      <DetailBody {...props} />
    </Modal>
  )
}

function DetailBody({
  item,
  tags,
  folderName,
  onEdit,
  onDelete,
}: ItemDetailProps) {
  const close = useModalClose()
  const [copied, setCopied] = useState(false)
  const isBookmark = item.type === 'bookmark'
  const href = isBookmark ? safeHref(item.content) : undefined
  const itemTags = tags.filter((t) => item.tagIds.includes(t.id))

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(item.content)
      setCopied(true)
      setTimeout(() => setCopied(false), 1600)
    } catch {
      /* clipboard unavailable */
    }
  }

  return (
    <>
      <div className="mb-3.5 flex items-center gap-3">
        <span
          className={`kind-ic ${isBookmark ? 'bm' : 'note'}`}
          aria-hidden="true"
        >
          {isBookmark ? (
            <Link2 className="h-[18px] w-[18px]" />
          ) : (
            <FileText className="h-[18px] w-[18px]" />
          )}
        </span>
        <span className="flex-1 text-[13px] font-semibold text-muted">
          {isBookmark ? 'Bookmark' : 'Note'}
          {folderName ? ` in ${folderName}` : ''}
        </span>
        <button
          type="button"
          className="icon-btn"
          aria-label="Close"
          onClick={close}
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <h2 className="mb-4 text-balance font-display text-[26px] font-semibold leading-tight tracking-tight sm:text-[30px]">
        {item.title}
      </h2>

      {isBookmark ? (
        <>
          <div className="flex items-center gap-2.5 rounded-[18px] border border-white/[0.09] bg-black/25 py-2 pl-4 pr-2">
            <span className="min-w-0 flex-1 truncate font-mono text-[13px] text-[#D8D7E4]">
              {item.content}
            </span>
            {href && (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary !min-h-[40px] !rounded-[13px] !px-4"
              >
                Open
                <ExternalLink className="h-4 w-4" />
              </a>
            )}
          </div>
          {item.description && (
            <p className="mt-4 text-base leading-relaxed text-[#DAD9E6]">
              {item.description}
            </p>
          )}
        </>
      ) : (
        <>
          {item.description && (
            <p className="mb-4 text-muted">{item.description}</p>
          )}
          <div className="note-prose">
            <ReactMarkdown
              components={{
                a: ({ node: _node, ...p }) => (
                  <a {...p} target="_blank" rel="noopener noreferrer" />
                ),
              }}
            >
              {item.content}
            </ReactMarkdown>
          </div>
        </>
      )}

      {itemTags.length > 0 && (
        <div className="mt-5 flex flex-wrap gap-2">
          {itemTags.map((t) => (
            <span key={t.id} className="chip">
              <span className="dot" style={{ background: t.color }} />
              {t.name}
            </span>
          ))}
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-2 border-t border-white/[0.09] pt-4">
        <span className="w-full text-[13px] text-faint sm:mr-auto sm:w-auto">
          Edited {formatDate(item.updatedAt)}
        </span>
        <button
          type="button"
          className="btn-secondary flex-1 sm:flex-none"
          onClick={copy}
        >
          {copied ? (
            <Check className="h-4 w-4 text-[#7FE0B0]" />
          ) : (
            <Copy className="h-4 w-4" />
          )}
          {copied ? 'Copied' : isBookmark ? 'Copy link' : 'Copy text'}
        </button>
        <button
          type="button"
          className="btn-secondary flex-1 sm:flex-none"
          onClick={onEdit}
        >
          <Pencil className="h-4 w-4" />
          Edit
        </button>
        <button
          type="button"
          className="btn-danger !px-3"
          aria-label="Delete"
          onClick={onDelete}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </>
  )
}
